import { motion } from "framer-motion";
import { FileText, ClipboardCheck, ArrowUpRight } from "lucide-react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { springs } from "../ui/motion";

interface Props {
  relatedEntityId: string;
  relatedEntityType: string;
  relatedEntityName: string;
}

export function RelatedEntityCard({
  relatedEntityId,
  relatedEntityType,
  relatedEntityName,
}: Props) {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const EntityIcon =
    relatedEntityType === "document" ? FileText : ClipboardCheck;

  const handleNavigate = () => {
    if (relatedEntityType === "document") {
      navigate({
        to: "/$workspaceSlug/documents/$documentId",
        params: {
          workspaceSlug: workspaceSlug ?? "",
          documentId: relatedEntityId,
        },
      });
    } else {
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: {
          workspaceSlug: workspaceSlug ?? "",
          auditId: relatedEntityId,
        },
      });
    }
  };

  return (
    <motion.button
      type="button"
      onClick={handleNavigate}
      className="group relative w-full rounded-[12px] border border-border/60 bg-card p-3.5 flex items-center gap-3 text-left mb-4 overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ ...springs.smooth, delay: 0.2 }}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, var(--detail-accent-soft, rgba(26,122,82,0.06)) 0%, transparent 60%)",
        }}
      />

      {/* Icon */}
      <div
        className="relative w-9 h-9 rounded-[8px] flex items-center justify-center shrink-0"
        style={{
          backgroundColor:
            "var(--detail-accent-soft, var(--color-accent-soft))",
        }}
      >
        <EntityIcon
          size={15}
          style={{
            color: "var(--detail-accent, var(--color-accent))",
          }}
        />
      </div>

      {/* Text */}
      <div className="relative flex-1 min-w-0">
        <p className="text-[11px] text-muted capitalize font-medium">
          {relatedEntityType}
        </p>
        <p className="text-[13px] font-medium text-primary truncate">
          {relatedEntityName}
        </p>
      </div>

      {/* Arrow — slides on hover */}
      <motion.div
        className="relative shrink-0 text-muted group-hover:text-secondary transition-colors duration-200"
        initial={false}
      >
        <ArrowUpRight
          size={15}
          className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </motion.div>
    </motion.button>
  );
}
